import type { Socket } from "socket.io-client";

type QueuedCommand = {
    event: string;
    payload: any;
    resolve: () => void;
    reject: (error: unknown) => void;
};

/* see docs/EXTRACTED_COMMENTS.md#c115 */
export class SocketCommandQueue {
    private queue: QueuedCommand[] = [];
    private locked = true;
    private draining = false;
    private socket: Socket | null = null;
    private _sessionId = 0;

    get sessionId(): number {
        return this._sessionId;
    }

    bind(socket: Socket): void {
        this.socket = socket;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c116 */
    emitAuthenticate(payload: any): void {
        if (!this.socket) return;
        this.socket.emit("authenticate", payload);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c117 */
    enqueue(event: string, payload: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.queue.push({ event, payload, resolve, reject });
            if (!this.locked) {
                this.drain();
            }
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c118 */
    send(event: string, payload: any): void {
        this.enqueue(event, payload).catch(() => {});
    }

    /* see docs/EXTRACTED_COMMENTS.md#c119 */
    unlock(): void {
        this.locked = false;
        this.drain();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c120 */
    lock(): void {
        this.locked = true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c121 */
    reset(): void {
        this._sessionId++;
        this.locked = true;
        this.draining = false;
        const pending = this.queue.splice(0, this.queue.length);
        for (const cmd of pending) {
            cmd.reject(new Error("SocketCommandQueue reset (session invalidated)"));
        }
    }

    /**
     * Drop pending commands without bumping session (used during clear).
     */
    clear(): void {
        const pending = this.queue.splice(0, this.queue.length);
        for (const cmd of pending) {
            cmd.reject(new Error("SocketCommandQueue cleared"));
        }
        this.draining = false;
    }

    get pending(): number {
        return this.queue.length;
    }

    private drain(): void {
        if (this.draining || this.locked || !this.socket) return;
        this.draining = true;

        while (this.queue.length > 0 && !this.locked) {
            const cmd = this.queue.shift()!;
            try {
                this.socket.emit(cmd.event, cmd.payload);
                cmd.resolve();
            } catch (error) {
                cmd.reject(error);
            }
        }

        this.draining = false;
    }
}
